import { useState, useEffect, useCallback } from 'react';

// Import APIs
import { getLinkedDevices } from '../../api/devices';

// Import utils
import { buildInitialDeviceId } from '../../utils/persistence';
import { logger } from '../../utils/logger';

export function useLinkedDevices() {
  const [linkedDeviceIds, setLinkedDeviceIds] = useState<string[]>([]);
  const [deviceId, setDeviceId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  const loadLinkedDevices = useCallback(async (id: string) => {
    try {
      const result = await getLinkedDevices(id);
      setLinkedDeviceIds(result.linkedDeviceIds);
    } catch (err) {
      logger.error('Failed to load linked devices:', err);
      // On error, just use the device ID itself
      setLinkedDeviceIds([id]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const id = buildInitialDeviceId();
    setDeviceId(id);
    loadLinkedDevices(id);
  }, [loadLinkedDevices]);

  const refreshLinkedDevices = useCallback(() => {
    if (deviceId) {
      loadLinkedDevices(deviceId);
    }
  }, [deviceId, loadLinkedDevices]);

  const isOwnerDevice = useCallback(
    (ownerDeviceId: string | undefined): boolean => {
      if (!ownerDeviceId) return false;
      if (ownerDeviceId === deviceId) return true;
      return linkedDeviceIds.includes(ownerDeviceId);
    },
    [deviceId, linkedDeviceIds],
  );

  const ownsParticipant = useCallback(
    (ownerDeviceId: string | undefined): boolean => {
      return isOwnerDevice(ownerDeviceId);
    },
    [isOwnerDevice],
  );

  return {
    deviceId,
    linkedDeviceIds,
    isLoading,
    isOwnerDevice,
    ownsParticipant,
    refreshLinkedDevices,
  };
}
